import { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import { Command, CommandEmpty, CommandGroup, CommandItem, CommandList } from "@/components/ui/command";
import { usePatients, usePatient, useAppointments, useClinicDoctors, useSlotBlocks, useSlotCapacity, usePublicAppSettings, useEffectiveSlotConfig, type Patient } from "@/lib/queries";
import { useCreateAppointment } from "@/lib/mutations";
import { isSlotBlocked, dayUnavailableReason } from "@emr/shared/scheduling";
import { fmtDate, fmtTime, todayIST, dateIST } from "@emr/shared/india";
import { Search, ChevronRight, ChevronLeft, UserPlus, CalendarClock, LoaderCircle } from "lucide-react";
import { toast } from "sonner";
import { useDropdownNav } from "@/hooks/useDropdownNav";
import { capFor } from "./slotCapacity";

// Shape of the 409 already_booked_today conflict returned by POST /appointments.
type AlreadyBookedConflict = {
  id: string;
  slot: string | null;
  arrivalTime: string | null;
  status: "Pending" | "Booked" | "CheckedIn" | "Completed" | "Cancelled" | "NoShow";
  reason: string | null;
};

export function BookDialog({ open, onOpenChange, patientId: preselectedPatientId }: { open: boolean; onOpenChange: (v: boolean) => void; patientId?: string | null }) {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [searchQ, setSearchQ] = useState("");
  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null);
  const [date, setDate] = useState(() => todayIST());
  // Slot = a KEY of the picked date's effective config (Phase 3).
  const [slot, setSlot] = useState<string | null>(null);
  const [arrival, setArrival] = useState("");
  const [reason, setReason] = useState("");
  const [whatsapp, setWhatsapp] = useState(true);
  // Same-day duplicate conflict surfaced by the API (409 already_booked_today).
  // When set, the dialog stays open and renders an alert with the existing
  // appt's slot/time so reception can tell the patient instead of re-booking.
  const [conflict, setConflict] = useState<AlreadyBookedConflict | null>(null);

  const { data: doctors } = useClinicDoctors();
  const createAppt = useCreateAppointment();

  // Preselected-patient fetch (patient-detail Book button): TanStack Query
  // instead of a raw one-shot apiFetch().then(). The old raw fetch had NO
  // error handling — a transient 503 (DB/Hyperdrive/Appwrite blip) left the
  // dialog silently stranded on the patient-search step with zero feedback,
  // and the booking "just didn't happen" (live incident 2026-10-13: two
  // just-created patients, bookings failed with no toast, no DB trace).
  // usePatient retries once on transient failures, shares the ["patients", id]
  // cache with the patient-detail page, and exposes isPending/isError so the
  // dialog can show a spinner / error instead of a silent dead end.
  const preselectedQ = usePatient(preselectedPatientId ?? undefined);
  const preselected = preselectedPatientId ? preselectedQ.data?.patient : undefined;

  useEffect(() => {
    if (open && preselectedPatientId) {
      // Advance to the slot step as soon as the preselected patient resolves.
      // Clear any stale selection first so a slow fetch for patient B can
      // never book patient A (the dialog re-renders with the old selection
      // while B is still loading).
      setSelectedPatient(null);
      setStep(0);
      if (preselected) {
        setSelectedPatient(preselected);
        setStep(1);
      }
    } else if (open && !preselectedPatientId) {
      setStep(0); setSelectedPatient(null);
    }
    // Clear any prior same-day conflict when the dialog reopens.
    if (open) setConflict(null);
  }, [open, preselectedPatientId, preselected]);

  const { data: searchResults } = usePatients(searchQ, 1, 8);
  const apptsQ = useAppointments(date);
  const slotBlocksQ = useSlotBlocks(date, date);
  const { data: capacity } = useSlotCapacity(date);
  const { data: settings } = usePublicAppSettings();
  // The PICKED DATE's effective config — slot buttons render from this
  // (label + session window), falling back to legacy defs from settings.
  const { slotList } = useEffectiveSlotConfig(date, settings);

  const patientSearchRef = useRef<HTMLInputElement>(null);
  const patientResults = searchResults?.rows ?? [];
  const { highlight: patientHighlight, highlightRef: patientHighlightRef, onKeyDown: onPatientSearchKeyDown } = useDropdownNav({
    count: searchQ.length >= 2 ? patientResults.length : 0,
    onSelect: (i) => { if (patientResults[i]) { setSelectedPatient(patientResults[i]); setStep(1); } },
    inputRef: patientSearchRef,
  });

  const maxDate = useMemo(() => {
    const d = new Date(); d.setMonth(d.getMonth() + 6); return dateIST(d);
  }, []);

  // Badge counts mirror the pages' definitions exactly (2026-10-13 fix):
  //  - "Confirmed" = Booked + CheckedIn + Completed — the same set the
  //    /appointments + /pending-appointments confirmed queues render.
  //    Previously this counted ONLY status === "Booked", so the badge fell to
  //    0/30 as soon as patients checked in — while the queue pages showed a
  //    full clinic (live evidence 2026-10-12: 22 Morning + 18 Evening, all
  //    Completed → badge read "Confirmed: 0/30").
  //  - "Pending" per slot = slot-bound pendings; review-flow pendings
  //    (slot=null, arrivalTime = review date) are counted separately and
  //    shown once under the slot grid — they have no slot, so attributing
  //    them to a button would be wrong, but omitting them entirely made the
  //    dialog read "Pending: 0" while the Pending Confirmations panel showed
  //    10 for the same date (live evidence 2026-10-13).
  //  Phase 3: counts are computed per CONFIG KEY from the day's rows; the
  //  capacity endpoint's slots map supplies booked/pending/max per key
  //  when present, with the legacy morning/evening fields as fallback.
  const counts = useMemo(() => {
    const rows = apptsQ.data?.rows ?? [];
    const confirmedBy = (s: string) => rows.filter((r) => r.slot === s && (r.status === "Booked" || r.status === "CheckedIn" || r.status === "Completed")).length;
    const pendingBy = (s: string) => rows.filter((r) => r.slot === s && r.status === "Pending").length;
    const reviewPending = rows.filter((r) => r.slot === null && r.status === "Pending").length;
    return { confirmedBy, pendingBy, reviewPending };
  }, [apptsQ.data]);

  // Per-key capacity: shared lookup (slotCapacity.ts) — prefer the Phase 2
  // slots map; fall back to the legacy top-level morning/evening fields when
  // the map is absent.

  // Day-level unavailability for the picked date (2026-10-07): off weekdays
  // (weekly schedule) and whole-day blocks disable BOTH slot buttons and the
  // Next step — previously the inline warning displayed but the dialog still
  // let the user pick a slot on, say, an off Sunday (the server would 422).
  const dateUnavailable = useMemo(() => {
    if (!date) return null;
    const wsAll = slotBlocksQ.data?.rows ?? [];
    const sched = (settings?.weeklySchedule as never) ?? undefined;
    return dayUnavailableReason(new Date(date + "T00:00:00"), sched, wsAll as any);
  }, [date, settings, slotBlocksQ.data]);

  const submit = async () => {
    if (!selectedPatient || !slot || !arrival) { toast.error("Patient, slot, and arrival time are required"); return; }
    const doctorsArr = Array.isArray(doctors) ? doctors : (doctors as any)?.rows ?? [];
    const doctor = doctorsArr[0];
    if (!doctor) { toast.error("No clinic doctor configured"); return; }
    const arrivalIso = new Date(`${date}T${arrival}:00`).toISOString();
    setConflict(null);
    try {
      await createAppt.mutateAsync({
        patientId: selectedPatient.id,
        doctorId: doctor.id,
        slot,
        arrivalTime: arrivalIso,
        status: "Booked",
        reason: reason || undefined,
        whatsappReminderEnabled: whatsapp,
      });
      onOpenChange(false);
      // Both confirmed (Booked) and capacity-downgraded (Pending) appointments
      // land on the Pending Appointments page, where the calendar + pending panel
      // + confirmed queue live. Navigate there so the user sees the result.
      navigate("/pending-appointments");
    } catch (e) {
      const err = e as Error & { body?: { error?: string; existing?: AlreadyBookedConflict } };
      // Same-day duplicate: keep the dialog open and surface the existing appt's
      // slot details so reception can inform the patient instead of re-booking.
      if (err?.body?.error === "already_booked_today" && err?.body?.existing) {
        setConflict(err.body.existing);
      }
      // Other errors are toasted by the mutation's onError.
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Book an appointment {step > 0 && `· Step ${step + 1}/3`}</DialogTitle>
        </DialogHeader>

        {step === 0 && (
          <div className="space-y-3">
            {/* Preselected-patient states (patient-detail Book button): the
                dialog opens with a patient id and must load it before the
                slot step. Show a spinner while loading and an inline error
                with a retry affordance on failure — never a silent dead end
                (the pre-fix behavior hid both states behind the search UI). */}
            {preselectedPatientId && preselectedQ.isPending && (
              <div className="flex items-center justify-center gap-2 p-4 text-sm text-muted">
                <LoaderCircle size={16} className="animate-spin" /> Loading patient…
              </div>
            )}
            {preselectedPatientId && preselectedQ.isError && (
              <div className="flex items-center justify-between gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-900 dark:bg-red-950/40 dark:text-red-400">
                <span>Couldn't load this patient. Check your connection and retry.</span>
                <Button size="sm" variant="outline" onClick={() => preselectedQ.refetch()}>Retry</Button>
              </div>
            )}
            {!preselectedPatientId && (
              <>
            <div className="relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted" />
              <Input
                ref={patientSearchRef}
                autoFocus
                placeholder="Search by phone, UHID, or name…"
                className="pl-9"
                value={searchQ}
                onChange={(e) => setSearchQ(e.target.value)}
                onKeyDown={onPatientSearchKeyDown}
              />
            </div>
            <div className="visible-scrollbar max-h-72 overscroll-contain overflow-y-auto rounded-lg border border-cream-200 bg-cream-100 dark:border-dark-700">
              {searchQ.length < 2 && <div className="p-4 text-center text-sm text-muted">Type at least 2 characters</div>}
              {searchQ.length >= 2 && patientResults.length === 0 && <div className="p-4 text-center text-sm text-muted">No patients found</div>}
              {patientResults.map((p, idx) => (
                <button
                  key={p.id}
                  type="button"
                  ref={idx === patientHighlight ? patientHighlightRef : undefined}
                  className={`flex w-full items-center justify-between border-b border-cream-200 px-3 py-2 text-left hover:bg-cream-200 dark:border-dark-700 dark:hover:bg-dark-700 ${
                    idx === patientHighlight ? "bg-cream-200 dark:bg-dark-700" : ""
                  }`}
                  onClick={() => { setSelectedPatient(p); setStep(1); }}
                >
                  <div>
                    <div className="text-sm font-medium">{[p.salutation, p.name].filter(Boolean).join(" ")}</div>
                    <div className="text-xs text-muted">{p.clinicUhid} · {p.phone ?? "no phone"}</div>
                  </div>
                  <ChevronRight size={16} className="text-muted" />
                </button>
              ))}
            </div>
            <Button variant="outline" className="w-full" onClick={() => { onOpenChange(false); navigate("/patients/new"); }}>
              <UserPlus size={14} className="mr-1" /> Add new patient
            </Button>
              </>
            )}
          </div>
        )}

        {step === 1 && selectedPatient && (
          <div className="space-y-4">
            <div className="rounded-lg bg-cream-100 p-3 text-sm dark:bg-dark-700">
              <div className="font-medium">{selectedPatient.name}</div>
              <div className="text-xs text-muted">{selectedPatient.clinicUhid} · {selectedPatient.phone ?? "—"}</div>
            </div>
            <div>
              <Label>Date</Label>
              <Input type="date" value={date} min={todayIST()} max={maxDate} onChange={(e) => setDate(e.target.value)} />
              {dateUnavailable && (
                <div className="mt-1 text-xs font-medium text-red-600">
                  {dateUnavailable.kind === "blocked"
                    ? `Whole day blocked${dateUnavailable.reason ? ` — ${dateUnavailable.reason}` : ""} (Settings → Block Days)`
                    : "Not a working day (Settings → Slot Config weekly schedule)"}
                </div>
              )}
            </div>
            <div>
              <Label>Slot</Label>
              <div className="grid grid-cols-2 gap-2">
                {slotList.map((def) => {
                  const ws = slotBlocksQ.data?.rows ?? [];
                  // A no-OPD day (off/blocked) makes ALL slots unselectable,
                  // not just slot-blocked ones.
                  const blocked = !!dateUnavailable || isSlotBlocked(new Date(date), def.key, ws as any);
                  const cap = capFor(capacity, def.key);
                  // Confirmed = Booked + CheckedIn + Completed (mirrors the
                  // queue pages) — NOT Booked-only, which read 0 once patients
                  // started checking in.
                  const booked = cap?.booked ?? counts.confirmedBy(def.key);
                  const pendingN = cap?.pending ?? counts.pendingBy(def.key);
                  const max = cap?.max;
                  const full = max !== undefined && booked >= max;
                  return (
                    <button
                      key={def.key}
                      type="button"
                      disabled={blocked}
                      onClick={() => { setSlot(def.key); setArrival(def.startTime); }}
                      className={`rounded-lg border p-3 text-left text-sm transition ${slot === def.key ? "border-maroon-600 bg-maroon-600/10" : "border-cream-200 hover:border-maroon-300 dark:border-dark-700"} ${blocked ? "opacity-40 cursor-not-allowed" : ""}`}
                    >
                      <div className="font-medium">{def.label}</div>
                      <div className="text-xs text-muted">
                        {blocked ? (
                          dateUnavailable
                            ? (dateUnavailable.kind === "blocked" ? "Day blocked" : "Clinic closed")
                            : "Blocked"
                        ) : (
                          <>
                            {def.startTime}–{def.endTime}
                            <br />
                            <span className={full ? "font-semibold text-amber-700" : ""}>
                              Confirmed: {booked}{max !== undefined ? `/${max}` : ""}
                            </span>
                            {" · "}
                            <span>Pending: {pendingN}</span>
                            {full && <span className="ml-1 text-amber-700">· full</span>}
                          </>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
              {counts.reviewPending > 0 && (
                <div className="text-xs text-muted">
                  + {counts.reviewPending} review-flow pending{counts.reviewPending === 1 ? "" : "s"} awaiting confirmation (no slot yet — see Pending Appointments page)
                </div>
              )}
            </div>
            <div className="flex justify-between">
              <Button variant="ghost" onClick={() => setStep(0)}><ChevronLeft size={14} /> Back</Button>
              <Button disabled={!slot || !!dateUnavailable} onClick={() => setStep(2)}>Next <ChevronRight size={14} /></Button>
            </div>
          </div>
        )}

        {step === 2 && selectedPatient && (
          <div className="space-y-4">
            {conflict && (
              <Alert variant="warning">
                <CalendarClock size={18} />
                <AlertTitle>Already booked for {fmtDate(date)}</AlertTitle>
                <AlertDescription>
                  <p>
                    This patient already has a <span className="font-semibold">{conflict.status}</span> appointment
                    {conflict.slot ? <> in the <span className="font-semibold">{conflict.slot}</span> slot</> : null}
                    {conflict.arrivalTime ? <> at <span className="font-semibold">{fmtTime(conflict.arrivalTime)}</span></> : null}
                    {conflict.reason ? <> · {conflict.reason}</> : null}.
                  </p>
                  <p className="mt-1">
                    {conflict.status === "Pending"
                      ? "This is a pending (unconfirmed) appointment — confirm it from the Pending Appointments page instead of booking again."
                      : "A patient can have only one appointment per day (morning or evening). Booking was refused."}
                  </p>
                </AlertDescription>
              </Alert>
            )}
            <div>
              <Label>Arrival time</Label>
              <Input type="time" value={arrival} onChange={(e) => { setArrival(e.target.value); setConflict(null); }} autoFocus />
            </div>
            <div>
              <Label>Reason (optional)</Label>
              <Input value={reason} onChange={(e) => { setReason(e.target.value); setConflict(null); }} placeholder="e.g. Follow-up" />
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={whatsapp} onChange={(e) => { setWhatsapp(e.target.checked); setConflict(null); }} />
              WhatsApp reminder
            </label>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setStep(1)}><ChevronLeft size={14} /> Back</Button>
              <Button onClick={submit} disabled={!arrival || createAppt.isPending} className="bg-maroon-600 hover:bg-maroon-700 text-cream-50">
                Book appointment
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
